import React, { useState } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Button } from "@UI/Components/Button/Button";
import { Flex } from "@UI/Components/Flex/Flex";
import { Heading } from "@UI/Components/Heading/Heading";
import { VStack } from "@UI/Components/Stack/Stack";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { useToast } from "@UI/hooks/useToast";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useDataQueryClient } from "@Libs/DataLayer/hooks/useDataQuery";
import { SingleSelectQuestion } from "../../Components/Questions/SingleSelectQuestion/SingleSelectQuestion";

import { serviceRequestPathFragment } from "../../../config/routes";
import { AutoFilledBadge } from "../../Components/Questions/AutoFilledBadge/AutoFilledBadge";
import { PrefillBanner } from "../../Components/PrefillBanner/PrefillBanner";
import { TaskCreationLayout } from "../../Components/TaskCreationLayout/TaskCreationLayout";
import { getSCategoryMessage } from "../../utils/messageKeys";
import { useTaskCreationFlow } from "../../Contexts/TaskCreationFlow";
import { usePCategory } from "../../hooks/useCategoryTree";
import { useDraftGoneRedirect } from "../../hooks/useDraftGoneRedirect";
import { useFlowBack } from "../../hooks/useFlowBack";
import {
  useFlowCategoryParams,
  useFlowPCategory,
} from "../../hooks/useFlowPCategory";
import {
  configQueryKey,
  useAiSuggestedSCategoryId,
  usePrefetchTaskCreationConfig,
} from "../../hooks/useServiceRequestDraft";
import { useSubmitScreenAnswers } from "../../hooks/useSubmitScreenAnswers";
import { useCurrentStepName } from "../../hooks/useTaskCreationSummary";
import messages from "../../messages";
import { ITaskCreationConfig } from "../../types";
import { buildFlowShape } from "../../utils/buildFlowShape";

const HEADING_ID = "subcategory-heading";

/**
 * The subcategory picker (RT-4321 M9): which of the theme's s_categories the
 * job belongs to.
 *
 * A frontend-owned screen, not a config one — the answer is a draft field
 * (`s_category_id`), and it is what decides which config the rest of the flow
 * runs on. Design gives no desktop frame, so the screen fills the column
 * `TaskCreationShell` gives it — the summary panel sits to its left from `lg`
 * up, mirroring the description screen's two-column shape.
 */
export const SubcategoryScreen = () => {
  const { formatMessage } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useDataQueryClient();
  const { isDesktop } = useMediaQuery();

  const {
    draft,
    answeredQuestions,
    totalQuestions,
    currentStepNumber,
    totalSteps,
    isLoggedIn,
    isFlowLoading,
  } = useTaskCreationFlow();
  const stepName = useCurrentStepName();

  // The resolved theme, not the draft's own `p_category_id`: that one is null
  // until a subcategory exists, which is exactly what this screen is for. So
  // this screen depends entirely on the URL — or, for a SEM visitor whose URL
  // names only an `s_category`, on the tree resolving the theme behind it.
  const { pCategoryId } = useFlowPCategory();
  const { sCategories, isLoading: isTreeLoading } = usePCategory(pCategoryId);

  const { submit, isLoading } = useSubmitScreenAnswers();
  const categoryParams = useFlowCategoryParams();
  const prefetchConfig = usePrefetchTaskCreationConfig();
  const handleDraftGone = useDraftGoneRedirect();

  // The draft's own persisted pick, if any — a resumed draft or a SEM
  // visitor's own choice, both of which outrank a mere suggestion below.
  const predicted = draft?.s_category_id
    ? String(draft.s_category_id)
    : undefined;

  // The AI's suggestion from the description POST (RT-4384) — seeded
  // client-side only, since the draft itself is never updated with it.
  const aiSuggested = useAiSuggestedSCategoryId(draft?.signed_id ?? null);
  const aiSuggestedValue =
    aiSuggested != null ? String(aiSuggested) : undefined;

  const [selected, setSelected] = useState<string>();
  const [error, setError] = useState<string>();

  const value = selected ?? predicted ?? aiSuggestedValue;
  // Only true while the AI's suggestion is what's actually preselected — not
  // once the visitor's own pick or a resumed draft's real one takes over.
  const isAiSuggestion = value !== undefined && value === aiSuggestedValue;

  const handleChange = (next: string) => {
    setSelected(next);
    setError(undefined);
    // Warm the config while the user is still reading, so Continue only waits
    // if they beat the network.
    prefetchConfig(Number(next));
  };

  const handleContinue = async () => {
    // Validate on click rather than disabling the CTA — a disabled button gives
    // no reason for being disabled.
    if (!value) {
      setError(formatMessage(messages["taskCreation.validation.required"]));
      return;
    }

    const sCategoryId = Number(value);

    try {
      await submit({ s_category_id: sCategoryId });
      // The next screen's key comes out of the chosen subcategory's own config,
      // so it has to be loaded before we can navigate.
      await prefetchConfig(sCategoryId);

      const config = queryClient.getQueryData<ITaskCreationConfig>(
        configQueryKey(sCategoryId)
      );
      // The first screen the chosen config contributes — `screens[0]` is this
      // picker itself, which every flow now carries (`buildFlowShape`).
      const next = config
        ? buildFlowShape(config, {}, { isLoggedIn }).screens.find(
            (screen) => screen.source === "config"
          )
        : undefined;

      if (!next) throw new Error("Config carries no screen to continue to");

      navigate(serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN, {
        withLocale: true,
        params: { screenKey: next.key },
        // Or the theme dies at this hop: `navigate` builds the path from
        // `params` alone (RT-4321).
        queryParams: categoryParams,
      });
    } catch (error) {
      if (handleDraftGone(error)) return;

      toast({
        status: "error",
        description: formatMessage(messages["taskCreation.error.saveFailed"]),
      });
    }
  };

  const handleBack = useFlowBack();

  const options = sCategories.map((node) => {
    // By id: `keyName` is the slug, not the key the names are keyed on.
    const message = getSCategoryMessage(node.id);
    const optionValue = String(node.id);

    return {
      value: optionValue,
      label: message ? formatMessage(message) : node.keyName,
      // Only the option actually standing in for the suggestion — not every
      // option matching `aiSuggestedValue` once the visitor has picked away
      // from it (`isAiSuggestion` already accounts for that).
      badge:
        isAiSuggestion && optionValue === aiSuggestedValue ? (
          <AutoFilledBadge />
        ) : undefined,
    };
  });

  return (
    <TaskCreationLayout
      answeredQuestions={answeredQuestions}
      totalQuestions={totalQuestions}
      step={currentStepNumber}
      totalSteps={totalSteps}
      stepName={stepName}
      // No options yet means nothing to pick: the heading and the CTAs on their
      // own read as a screen with an empty list rather than one still loading.
      isLoading={isFlowLoading || isTreeLoading}
    >
      <Box paddingY="16px">
        <VStack alignItems="stretch" spacing="16px">
          {isAiSuggestion && <PrefillBanner />}
          <Heading
            as="h1"
            id={HEADING_ID}
            fontFamily="heading"
            fontSize={{ base: "3xl", lg: "5xl" }}
            lineHeight="1"
            color="gray.800"
          >
            <FormattedMessage
              {...messages["taskCreation.subcategory.heading"]}
            />
          </Heading>
          {/* The `<h1>` is not the group's label, so the group names itself by
                pointing at it — one string rather than a second, duplicate one. */}
          <Box as="fieldset" aria-labelledby={HEADING_ID}>
            <SingleSelectQuestion
              name="s_category_id"
              options={options}
              value={value}
              onChange={handleChange}
              isDisabled={isLoading}
              isInvalid={Boolean(error)}
              errorMessage={error}
              spacing="16px"
              columns={isDesktop ? 2 : 1}
            />
          </Box>
          <Flex gap="8px" paddingTop="16px" justifyContent="flex-end">
            <Button
              onClick={handleBack}
              isDisabled={isLoading}
              background="gray.300"
              color="gray.800"
              height="48px"
              borderRadius="10px"
              paddingX="22px"
              fontSize="md"
            >
              <FormattedMessage {...messages["taskCreation.cta.back"]} />
            </Button>
            <Button
              onClick={handleContinue}
              isLoading={isLoading}
              isDisabled={isLoading}
              colorScheme="seaGreen"
              height="48px"
              borderRadius="10px"
              paddingX="22px"
              fontSize="md"
              flex={{ base: "1", lg: "0 0 auto" }}
            >
              <FormattedMessage {...messages["taskCreation.cta.continue"]} />
            </Button>
          </Flex>
        </VStack>
      </Box>
    </TaskCreationLayout>
  );
};
